import { supabase } from '../lib/supabase';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Conversation, Content } from '@shared/types';

const defaultConversation: Conversation = {
  id: '',
  title: '',
  current_message_leaf_id: null,
  user_id: '',
  created_at: null,
  updated_at: null,
};

export function useConversation(conversationId: string | null) {
  const queryClient = useQueryClient();

  const { data: conversation, isLoading: isConversationLoading } =
    useQuery<Conversation>({
      queryKey: ['conversation', conversationId],
      enabled: !!conversationId,
      refetchOnMount: false,
      queryFn: async () => {
        if (!conversationId) {
          throw new Error('Conversation ID is required');
        }

        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user?.id) {
          throw new Error('User must be authenticated');
        }

        const { data, error } = await supabase
          .from('conversations')
          .select('*')
          .eq('id', conversationId)
          .eq('user_id', userData.user.id)
          .limit(1)
          .single();

        if (error) {
          throw error;
        }
        return data as Conversation;
      },
    });

  const { mutate: updateConversation, mutateAsync: updateConversationAsync } =
    useMutation({
      mutationFn: async (conversation: Conversation) => {
        const { data, error } = await supabase
          .from('conversations')
          .update(conversation)
          .eq('id', conversation.id)
          .select()
          .single();

        if (error) {
          throw error;
        }

        return data as Conversation;
      },
      onMutate: async (conversation) => {
        await queryClient.cancelQueries({
          queryKey: ['conversation', conversation.id],
        });

        const oldConversation = queryClient.getQueryData<Conversation>([
          'conversation',
          conversation.id,
        ]);

        queryClient.setQueryData(
          ['conversation', conversation.id],
          conversation,
        );

        return { oldConversation };
      },
      onSuccess: (data) => {
        queryClient.setQueryData(['conversation', conversationId], data);
        queryClient.invalidateQueries({
          queryKey: ['conversations'],
        });
      },
      onError: (_error, conversation, context) => {
        queryClient.setQueryData(
          ['conversation', conversation.id],
          context?.oldConversation,
        );
      },
    });

  return {
    conversation: conversation ?? defaultConversation,
    isConversationLoading,
    updateConversation,
    updateConversationAsync,
  };
}

export async function createConversation(title: string = 'New Design'): Promise<Conversation> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user?.id) {
    throw new Error('User must be authenticated');
  }

  const { data, error } = await supabase
    .from('conversations')
    .insert({
      title,
      user_id: userData.user.id,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data as Conversation;
}

export function useConversationsQuery() {
  return useQuery<Conversation[]>({
    queryKey: ['conversations'],
    queryFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        return [];
      }

      const { data, error } = await supabase
        .from('conversations')
        .select('*')
        .eq('user_id', userData.user.id)
        .order('updated_at', { ascending: false })
        .limit(50);

      if (error) {
        throw error;
      }

      return (data || []) as Conversation[];
    },
  });
}

export async function generateConversationTitle(
  conversationId: string,
  content: Content,
): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('No active session');
  }

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/title-generator`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        content,
        conversationId,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to generate title: ${response.statusText}`);
  }

  const data = await response.json();
  return data.title || 'New Conversation';
}
